import { getCurrentClientId, getMobileSession } from '@Api/idv.api';
import { resolveDeviceAuthEnabled } from '@Use/useDeviceAuthFlag';
import { SessionExpiredError } from '@Utils/sessionExpired';

export interface ResolvedAuthQueryParam {
    temporarySessionId?: string;
    clientId?: string;
}

/**
 * Dependencies injected into {@link resolveAuthQueryParam}. Default to the real auth-MFE
 * implementations; the unit tests pass hand-written stubs (per the
 * `@certifid/testing/no-transient-mocks` lint rule) to exercise each branch.
 */
export interface AuthQueryParamDeps {
    getMobileSession: typeof getMobileSession;
    getCurrentClientId: typeof getCurrentClientId;
    resolveDeviceAuthEnabled: typeof resolveDeviceAuthEnabled;
}

const DEFAULT_DEPS: AuthQueryParamDeps = {
    getMobileSession,
    getCurrentClientId,
    resolveDeviceAuthEnabled,
};

export interface AuthQueryParamResult {
    /** The auth fragment to inject into the route query, or `null` when both paths failed. */
    resolved: ResolvedAuthQueryParam | null;
    /** The LD flag state at resolution time — surfaced so the router can include it in the
     * total-failure log, letting Datadog filter V0-mode vs. legacy-mode failures (PR Buddy
     * feedback on certifid-v2-client-hub-auth-mfe#182). */
    deviceAuthEnabled: boolean;
    /** True when getMobileSession answered 404: the session is gone, so the router sends the
     * user to the session-expired screen instead of not-found. */
    sessionExpired?: boolean;
}

/**
 * DS-3208 — when a route requires identity in the query (e.g. `verify-identity-instructions`),
 * the router used to single-shot whichever bootstrap path matched the device-auth LD flag and
 * `redirectToNotFound()` on the first failure. Two prod conditions made that too brittle:
 *
 *   1. V1-grandparent / public-API IDV requests land with no `CertifidClientId` on the recipient
 *      (resolution in v2-bff's `SubmitIdentityRequestPipeline` is gated on the V0 flag; the
 *      other creation paths skip it entirely). So `activitySession.ClientId` is null, the
 *      legacy `getCurrentClientId` returns 404, and the recipient sees "Page Not Found".
 *      Audit + warehouse pull on DS-3208 turned up ~6,800 stuck records across 9 real
 *      customer companies.
 *   2. Company-level config drift (partial V0 rollout) leaves the LD flag and the
 *      portal-enabled state out of sync, with no graceful path for the half-configured state.
 *
 * This helper tries the LD-preferred path first and falls back to the other before returning
 * `null`. The caller decides what to do with `null` (the router 404s as a last resort, with
 * an error log enriched with `deviceAuthEnabled` for Datadog filtering). When the fallback
 * fires, a warning fires so the count is observable.
 */
export async function resolveAuthQueryParam(
    deps: AuthQueryParamDeps = DEFAULT_DEPS,
): Promise<AuthQueryParamResult> {
    const deviceAuthEnabled = await deps.resolveDeviceAuthEnabled();

    // GetMobileSession answers 404 when the session is gone. Then neither path can work, so
    // stop trying and report it.
    let sessionExpired = false;

    const tryMobileSession = async (): Promise<ResolvedAuthQueryParam | null> => {
        let response;
        try {
            response = await deps.getMobileSession();
        } catch (error) {
            if (!(error instanceof SessionExpiredError)) throw error;
            sessionExpired = true;
            return null;
        }
        return response?.temporarySessionId ? { temporarySessionId: response.temporarySessionId } : null;
    };

    const tryClientId = async (): Promise<ResolvedAuthQueryParam | null> => {
        const clientId = await deps.getCurrentClientId();
        return clientId ? { clientId } : null;
    };

    const primary = deviceAuthEnabled ? tryMobileSession : tryClientId;
    const fallback = deviceAuthEnabled ? tryClientId : tryMobileSession;

    const primaryResult = await primary();
    if (primaryResult) return { resolved: primaryResult, deviceAuthEnabled };
    if (sessionExpired) return { resolved: null, deviceAuthEnabled, sessionExpired };

    const fallbackResult = await fallback();
    if (fallbackResult) {
        console.warn(
            '[Auth MFE] Primary auth bootstrap failed; recovered via fallback path (DS-3208).',
            { deviceAuthEnabled, recovered: deviceAuthEnabled ? 'getCurrentClientId' : 'getMobileSession' },
        );
        return { resolved: fallbackResult, deviceAuthEnabled };
    }
    if (sessionExpired) return { resolved: null, deviceAuthEnabled, sessionExpired };

    return { resolved: null, deviceAuthEnabled };
}
